/**
 * pipeline/queue.ts
 *
 * Per-session, in-process prompt queue.
 * drain() runs until dispose() is called — session lifetime scoped.
 * No Effect TS. Plain async/await + Promise. Private class fields (#).
 */

import type { PromptTask } from "./types";

// ─── Interface ────────────────────────────────────────────────────────────────

export interface PromptQueue {
  enqueue(task: PromptTask): void;
  /** Yields tasks sequentially. Runs until dispose() is called. */
  drain(): AsyncGenerator<PromptTask>;
  dispose(): void;
}

// ─── Implementation ───────────────────────────────────────────────────────────

export class InProcessPromptQueue implements PromptQueue {
  #queue: PromptTask[] = [];
  #waiting: ((task: PromptTask | null) => void) | null = null;
  #disposed = false;

  enqueue(task: PromptTask): void {
    if (this.#disposed) {
      throw new Error("PromptQueue: disposed");
    }
    if (this.#waiting !== null) {
      const resolve = this.#waiting;
      this.#waiting = null;
      resolve(task);
    } else {
      this.#queue.push(task);
    }
  }

  async *drain(): AsyncGenerator<PromptTask> {
    while (true) {
      // Drain any already-queued tasks first.
      while (this.#queue.length > 0) {
        yield this.#queue.shift()!;
      }

      if (this.#disposed) {
        return;
      }

      // Block until a task arrives or dispose() is called.
      const task = await new Promise<PromptTask | null>((resolve) => {
        this.#waiting = resolve;
      });

      if (task === null) {
        // dispose() signalled — generator is done.
        return;
      }

      yield task;
    }
  }

  dispose(): void {
    this.#disposed = true;
    if (this.#waiting !== null) {
      const resolve = this.#waiting;
      this.#waiting = null;
      resolve(null);
    }
  }
}
